import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Pipe,
  PipeTransform,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { AgentChatService, ChatSummary, RecordItem, TurnTimings } from '../../../data/service/agent-chat.service';
import { OrganizationService } from '../../../data/service/organization.service';
import { AiChatMessage, AiChatStateService, AiChatToolStep } from './ai-chat-state.service';
import { AiSuggestionsService, GenericSuggestions } from './ai-suggestions.service';
import { ChatStatementComponent } from './blocks/chat-statement.component';

@Pipe({ name: 'markdown' })
export class MarkdownPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string): SafeHtml {
    if (!value) return '';
    let html = this.escapeHtml(value);

    // Code blocks (```)
    html = html.replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>');
    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
    // Bold
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Italic
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    // Links [text](url)
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
    // Headings (###, ##, #) — only at line start
    html = html.replace(/^### (.+)$/gm, '<h4>$1</h4>');
    html = html.replace(/^## (.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^# (.+)$/gm, '<h2>$1</h2>');

    // Numbered lists: convert consecutive lines starting with "1. ", "2. " etc.
    html = html.replace(/((?:^|\n)\d+\. .+(?:\n   .+)*)+/g, (match) => {
      const items = match.trim().split(/\n(?=\d+\. )/);
      const lis = items.map(item => {
        const content = item.replace(/^\d+\.\s/, '');
        return `<li>${content.replace(/\n   /g, '<br>')}</li>`;
      }).join('');
      return `<ol>${lis}</ol>`;
    });

    // Unordered lists: lines starting with "- "
    html = html.replace(/((?:^|\n)- .+(?:\n  .+)*)+/g, (match) => {
      const items = match.trim().split(/\n(?=- )/);
      const lis = items.map(item => {
        const content = item.replace(/^- /, '');
        return `<li>${content.replace(/\n  /g, '<br>')}</li>`;
      }).join('');
      return `<ul>${lis}</ul>`;
    });

    // Paragraphs: double newlines
    html = html.replace(/\n\n/g, '</p><p>');
    // Single newlines (not inside lists/pre) become <br>
    html = html.replace(/\n/g, '<br>');
    // Wrap in paragraph
    html = `<p>${html}</p>`;
    // Clean up empty paragraphs and paragraph wrapping around block elements
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p>(<(?:ol|ul|h[234]|pre))/g, '$1');
    html = html.replace(/(<\/(?:ol|ul|h[234]|pre)>)<\/p>/g, '$1');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}


interface RecordGroup {
  key: string;
  records: RecordItem[];
}

export function humaniseKey(key: string): string {
  const words = key
    .replace(/Id$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_.]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

/** An answer as plain text: its text blocks, tables as tab-separated rows, statements line by line. */
export function plainText(msg: AiChatMessage): string {
  if (!msg.blocks?.length) { return msg.text || ''; }
  return msg.blocks.map(b => {
    switch (b.type) {
      case 'text': return b.text;
      case 'table': return [b.table.headers, ...b.table.rows].map(r => r.join('\t')).join('\n');
      case 'records': return b.records.map(r => r.title).join('\n');
      case 'statement':
        return [b.statement.report, ...ChatStatementComponent.flatten(b.statement)
          .map(r => r.amounts.length ? `${r.label}\t${r.amounts.join('\t')}` : r.label)].join('\n');
      default: return '';
    }
  }).filter(t => !!t).join('\n\n');
}

/** "0.4s", "12s", "1m 5s". */
function formatDuration(ms: number): string {
  if (ms < 10000) { return `${(Math.max(ms, 0) / 1000).toFixed(1)}s`; }
  const seconds = Math.round(ms / 1000);
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

/** "Routing 0.4s · Setup 0.2s · Lookups 1.1s · First word 5.2s · Total 7.0s" for admins. */
function describeTimings(t: TurnTimings): string {
  const parts: [string, number | null | undefined][] = [
    ['Sign-in', t.signInMs], ['Routing', t.routeMs], ['Setup', t.setupMs], ['Lookups', t.lookupMs], ['First model call', t.modelMs],
    ['First word', t.firstWordMs], ['Total', t.totalMs],
  ];
  const times = parts.filter(([, ms]) => typeof ms === 'number').map(([name, ms]) => `${name} ${formatDuration(ms as number)}`);
  const who = [t.agentId ? `Agent ${t.agentId}` : '', t.model ? `Model ${t.model}` : ''].filter(x => !!x);
  return [...times, ...who].join(' · ');
}

/** Earlier chats grouped by when they were last used. Exported for tests. */
export function groupChats(chats: ChatSummary[], now: Date): { label: string; chats: ChatSummary[] }[] {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const day = 24 * 60 * 60 * 1000;
  const groups: { label: string; chats: ChatSummary[] }[] = [
    { label: 'Today', chats: [] }, { label: 'Yesterday', chats: [] },
    { label: 'Previous 7 days', chats: [] }, { label: 'Older', chats: [] },
  ];
  for (const chat of chats) {
    const at = new Date(chat.lastAt).getTime();
    const index = at >= startOfToday ? 0 : at >= startOfToday - day ? 1 : at >= startOfToday - 7 * day ? 2 : 3;
    groups[index].chats.push(chat);
  }
  return groups.filter(g => g.chats.length);
}

/** "assistant" → "Assistant", "billing-agent" → "Billing agent". */
function agentDisplayName(agentId: string): string {
  const words = (agentId || '').replace(/[-_.]+/g, ' ').trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

/** Errors that are a refusal the user can't fix by retrying: shown with the Roles & access hint. */
const RefusalCodes = ['ai_disabled', 'access_denied', 'role_not_allowed', 'forbidden'];

@Component({
  selector: 'app-ai-chat-panel',
  templateUrl: './ai-chat-panel.component.html',
  styleUrls: ['./ai-chat-panel.component.scss']
})
export class AiChatPanelComponent implements OnChanges, OnInit, OnDestroy {
  @Input() isOpen: boolean = false;
  @Input() initialQuery: string = '';
  @Output() closed = new EventEmitter<void>();

  @ViewChild('messagesContainer') messagesContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('inputRef') inputRef!: ElementRef<HTMLTextAreaElement>;

  inputText: string = '';
  isLoading: boolean = false;
  streamingText: string = '';

  /** SD-1244: the tools the AI is using in the turn under way. */
  liveTools: AiChatToolStep[] = [];
  /** Text the user copied, briefly, for the "Copied" tick. */
  copiedIndex: number | null = null;

  // ── Input history (shell-style ↑/↓ recall) ──
  // historyIndex === -1 means "not navigating" (showing the live draft);
  // otherwise it points into the shared input history.
  private historyIndex: number = -1;
  private draftBeforeHistory: string = '';

  /** The turn under way: when it started, and the time now (ticks while it runs). */
  runStartedAt = 0;
  now = 0;
  /** SD-1244: the agent answering the turn under way, e.g. "Assistant". */
  agentName = '';
  private runTimer: any = null;
  /** Admin-only: why the turn had no Synced tools, shown once the turn ends. */
  private toolsNote: string | null = null;
  /** Admin-only: where the turn's time went. */
  private timings: string | null = null;
  private abort: AbortController | null = null;
  private subscriptions = new Subscription();

  suggestions: string[] = GenericSuggestions;

  /** SD-1244: the chat, or the list of the user's earlier chats. */
  view: 'chat' | 'history' = 'chat';
  chats: ChatSummary[] = [];
  chatsLoading = false;
  chatsError = '';
  openingChat: string | null = null;
  openingTitle = '';
  /** Why an earlier chat could not be opened (shown in the chat with a way back to the list). */
  openError = '';

  constructor(
    private agentChatService: AgentChatService,
    private ngZone: NgZone,
    public state: AiChatStateService,
    private suggestionsService: AiSuggestionsService,
    private organizationService: OrganizationService,
    private host: ElementRef<HTMLElement>
  ) {}

  /** The conversation (shared by every chat panel, kept across pages). */
  get messages(): AiChatMessage[] {
    return this.state.messages;
  }

  /** The organisation the chat works in: the one the user is signed in to. */
  get organisationName(): string {
    return this.state.organisationName || this.organizationService.selectedOrganizationName || '';
  }

  ngOnInit(): void {
    // The panel and its backdrop live at the top of the page, so the backdrop covers the whole
    // app, the left menu included (the content area is its own stacking layer below the menu).
    document.body.appendChild(this.host.nativeElement);
    this.subscriptions.add(this.suggestionsService.suggestions$.subscribe(list => this.suggestions = list));
    // A different organisation is a different conversation: the chat never spans two.
    this.subscriptions.add(this.organizationService.selectedOrganizationId.subscribe(id => {
      const orgId = id || this.organizationService.selectedOrganization || null;
      const changed = this.state.useOrganisation(orgId, this.organizationService.selectedOrganizationName || '');
      if (changed) {
        this.stop();
        this.state.messages.push({
          role: 'notice', text: `Now working in ${this.organisationName || 'the selected organisation'}.`, timestamp: new Date()
        });
      }
    }));
  }

  ngOnDestroy(): void {
    this.host.nativeElement.remove();
    this.subscriptions.unsubscribe();
    this.stopRunTimer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true) {
      setTimeout(() => {
        if (this.initialQuery?.trim()) {
          // Adds to the conversation; New chat starts afresh.
          this.historyIndex = -1;
          this.inputText = this.initialQuery;
          this.send();
        } else {
          this.inputRef?.nativeElement?.focus();
        }
        this.scrollToBottom();
      }, 310);
    }
  }

  close(): void {
    this.closed.emit();
  }

  /** Shows the user's earlier chats in this organisation. */
  async showHistory(): Promise<void> {
    this.view = 'history';
    this.chatsLoading = true;
    this.chatsError = '';
    try {
      const chats = await this.agentChatService.listChats();
      this.ngZone.run(() => { this.chats = chats; });
    } catch (e) {
      this.ngZone.run(() => { this.chatsError = (e as Error)?.message || 'Could not load your chats.'; });
    } finally {
      this.ngZone.run(() => { this.chatsLoading = false; });
    }
  }

  /** Back to the conversation. */
  showChat(): void {
    this.view = 'chat';
    setTimeout(() => this.inputRef?.nativeElement?.focus());
  }

  /** Opens an earlier chat; the next message carries on from it. */
  async openChat(chat: ChatSummary): Promise<void> {
    if (this.openingChat) { return; }
    if (chat.sessionId === this.state.sessionId) { this.showChat(); return; }
    // Straight into the chat, showing it is opening, so a tap always visibly does something.
    this.stop();
    this.state.reset();
    this.openError = '';
    this.openingChat = chat.sessionId;
    this.openingTitle = chat.title;
    this.view = 'chat';
    try {
      const record = await this.agentChatService.getChat(chat.sessionId);
      this.ngZone.run(() => {
        if (this.openingChat !== chat.sessionId) { return; }
        for (const turn of record.turns || []) {
          const at = new Date(turn.createdAt);
          if (turn.userMessage) {
            this.state.messages.push({ role: 'user', text: turn.userMessage, timestamp: at });
            this.state.lastUserMessage = turn.userMessage;
          }
          const tools = (turn.tools || []).map(t => ({ label: t.label, ok: t.ok, ms: t.ms }));
          this.state.messages.push({
            role: 'ai',
            text: turn.assistantMessage || '',
            // Statements, tables and records as they were shown; older turns come back as text.
            blocks: !turn.isError && turn.blocks?.length ? turn.blocks : undefined,
            timestamp: at,
            tools: tools.length ? tools : undefined,
            runMs: turn.durationMs ?? undefined,
            error: turn.isError ? {} : undefined,
          });
        }
        this.state.sessionId = record.sessionId;
        this.state.resolvedAgentId = record.agentId || null;
        this.scrollToBottom();
      });
    } catch (e) {
      this.ngZone.run(() => { this.openError = (e as Error)?.message || 'Could not open that chat.'; });
    } finally {
      this.ngZone.run(() => {
        if (this.openingChat === chat.sessionId) { this.openingChat = null; }
      });
    }
  }

  /** The earlier chats, grouped: Today, Yesterday, Previous 7 days, Older. */
  get chatGroups(): { label: string; chats: ChatSummary[] }[] {
    return groupChats(this.chats, new Date());
  }

  /** Starts a new conversation in the same organisation. */
  newChat(): void {
    this.view = 'chat';
    this.openError = '';
    this.openingChat = null;
    this.stop();
    this.state.reset();
    this.streamingText = '';
    this.liveTools = [];
    this.inputText = '';
    this.inputRef?.nativeElement?.focus();
  }

  sendSuggestion(text: string): void {
    this.inputText = text;
    this.send();
  }

  send(): void {
    const text = this.inputText.trim();
    if (!text || this.isLoading) { return; }

    // A fresh message answers no outstanding confirmation: it lapses.
    const pending = this.state.pendingConfirmation;
    if (pending) {
      pending.confirmationState = 'cancelled';
      pending.decidedAt = new Date();
    }

    this.state.messages.push({ role: 'user', text, timestamp: new Date() });
    // Record for ↑/↓ recall (skip consecutive duplicates) and reset navigation.
    const history = this.state.inputHistory;
    if (history[history.length - 1] !== text) {
      history.push(text);
    }
    this.historyIndex = -1;
    this.draftBeforeHistory = '';
    this.inputText = '';
    this.resetInputHeight();
    this.state.lastUserMessage = text;
    // First turn sends no agentId — the backend routes by intent and echoes the
    // resolved agent back to us (captured in onDone/onConfirm).
    this.runTurn(text);
  }

  /** Confirms a write the AI asked about: resends the turn with the action confirmed. */
  confirmAction(msg: AiChatMessage): void {
    if (!msg.confirmation || msg.confirmationState !== 'pending' || this.isLoading) { return; }
    msg.confirmationState = 'confirmed';
    msg.decidedAt = new Date();
    this.runTurn(this.state.lastUserMessage, this.state.resolvedAgentId ?? undefined, [msg.confirmation.action]);
  }

  /** Declines a write. The agent is told too, so its history matches what happened. */
  cancelConfirmation(msg: AiChatMessage): void {
    if (msg.confirmationState !== 'pending' || this.isLoading) { return; }
    msg.confirmationState = 'cancelled';
    msg.decidedAt = new Date();
    this.runTurn('Cancel that — don\'t make the change.', this.state.resolvedAgentId ?? undefined, undefined, true);
  }

  /** A confirmation's parameters as labelled fields: "invoiceId" reads "Invoice". */
  confirmationParams(msg: AiChatMessage): { key: string; value: string }[] {
    const params = msg.confirmation?.parameters || {};
    return Object.keys(params).map(key => {
      const raw = (params as Record<string, any>)[key];
      const value = raw !== null && typeof raw === 'object' ? JSON.stringify(raw) : String(raw ?? '');
      return { key: humaniseKey(key), value };
    });
  }


  /** Stops the turn under way. What arrived so far stays. */
  stop(): void {
    if (!this.abort) { return; }
    this.abort.abort();
    this.abort = null;
    this.finishTurn(this.streamingText ? { role: 'ai', text: this.streamingText, timestamp: new Date() } : null, true);
  }

  retry(msg: AiChatMessage): void {
    const text = msg.error?.retryText;
    if (!text || this.isLoading) { return; }
    this.inputText = text;
    this.send();
  }

  isRefusal(msg: AiChatMessage): boolean {
    return !!msg.error?.code && RefusalCodes.indexOf(msg.error.code) >= 0;
  }

  copy(msg: AiChatMessage, index: number): void {
    const text = plainText(msg);
    const done = () => this.ngZone.run(() => {
      this.copiedIndex = index;
      setTimeout(() => this.ngZone.run(() => { if (this.copiedIndex === index) { this.copiedIndex = null; } }), 1500);
    });
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done, () => {});
    }
  }


  /** Right-aligns a table column that holds amounts. */
  isNumericColumn(rows: string[][], index: number): boolean {
    const values = rows.map(r => (r[index] || '').trim()).filter(v => !!v);
    return values.length > 0 && values.every(v => /^[^\d\-(]{0,4}[-(]?[\d,]+(\.\d+)?\)?%?$/.test(v));
  }

  // Runs a single chat turn. `agentId`/`confirmedActions` are only set on a
  // confirm resend; normal turns leave them undefined.
  private runTurn(text: string, agentId?: string, confirmedActions?: string[], quiet = false): void {
    if (quiet) {
      this.state.messages.push({ role: 'notice', text: 'Cancelled — nothing was changed.', timestamp: new Date() });
    }
    this.isLoading = true;
    this.streamingText = '';
    this.liveTools = [];
    this.toolsNote = null;
    this.timings = null;
    this.agentName = '';
    this.startRunTimer();
    this.scrollToBottom();
    const abort = new AbortController();
    this.abort = abort;
    const live = () => this.abort === abort;

    this.agentChatService.stream(
      { message: text, sessionId: this.state.sessionId, agentId, confirmedActions, signal: abort.signal },
      {
        onContext: e => this.ngZone.run(() => {
          if (e.organisationName) { this.state.organisationName = e.organisationName; }
        }),
        onAgent: id => this.ngZone.run(() => {
          this.state.resolvedAgentId = id;
          this.agentName = agentDisplayName(id);
        }),
        onTool: e => this.ngZone.run(() => {
          if (!live()) { return; }
          if (e.phase === 'start') {
            this.liveTools.push({ label: e.label, detail: e.detail, ok: null, startedAt: Date.now() });
          } else {
            const step = [...this.liveTools].reverse().find(t => t.label === e.label && t.ok === null);
            const ms = e.ms ?? (step?.startedAt ? Date.now() - step.startedAt : undefined);
            if (step) {
              step.ok = e.ok !== false;
              step.ms = ms;
            } else {
              this.liveTools.push({ label: e.label, detail: e.detail, ok: e.ok !== false, ms });
            }
          }
          this.scrollToBottom();
        }),
        onToolsStatus: status => this.ngZone.run(() => {
          if (!live() || status.reason === 'ok') { return; }
          this.toolsNote = `Admin note · Synced tools unavailable — ${status.reason}` +
            (status.count ? ` (${status.count} available)` : '');
        }),
        onTimings: t => this.ngZone.run(() => {
          if (!live()) { return; }
          this.timings = describeTimings(t);
        }),
        onChunk: chunk => this.ngZone.run(() => {
          if (!live()) { return; }
          this.streamingText += chunk;
          this.scrollToBottom();
        }),
        onBlocks: blocks => this.ngZone.run(() => {
          if (!live() || quiet) { return; }
          this.streamingText = '';
          this.state.messages.push({ role: 'ai', text: '', blocks, timestamp: new Date(), tools: this.takeTools() });
          this.scrollToBottom();
        }),
        onDone: (sessionId, resolvedAgentId) => this.ngZone.run(() => {
          if (!live()) { return; }
          this.state.sessionId = sessionId || this.state.sessionId;
          if (resolvedAgentId) { this.state.resolvedAgentId = resolvedAgentId; }
          // Streamed text that never got blocks is the answer itself.
          const answer = quiet ? '' : this.streamingText;
          this.finishTurn(answer ? { role: 'ai', text: answer, timestamp: new Date(), tools: this.takeTools() } : null);
        }),
        onError: (errorMsg, code) => this.ngZone.run(() => {
          if (!live()) { return; }
          this.finishTurn({
            role: 'ai',
            text: errorMsg,
            timestamp: new Date(),
            tools: this.takeTools(),
            error: { code, retryText: quiet ? undefined : text },
          });
        }),
        // A write skill needs confirmation before it runs: kept in the thread as a card.
        onConfirm: (req, sessionId, resolvedAgentId) => this.ngZone.run(() => {
          if (!live()) { return; }
          if (sessionId) { this.state.sessionId = sessionId; }
          if (resolvedAgentId) { this.state.resolvedAgentId = resolvedAgentId; }
          this.streamingText = '';
          this.state.messages.push({
            role: 'confirm', text: req.prompt, confirmation: req, confirmationState: 'pending',
            timestamp: new Date(), tools: this.takeTools(),
          });
          this.finishTurn(null);
        }),
      });
  }

  /** The tools used so far this turn, handed to the message that answers it. */
  private takeTools(): AiChatToolStep[] | undefined {
    const tools = this.liveTools.map(t => ({ label: t.label, detail: t.detail, ms: t.ms, ok: t.ok === null ? true : t.ok }));
    this.liveTools = [];
    return tools.length ? tools : undefined;
  }

  private finishTurn(message: AiChatMessage | null, stopped = false): void {
    const runMs = this.runStartedAt ? Date.now() - this.runStartedAt : undefined;
    // The answer (or the confirmation card) already in the thread this turn takes the run too.
    const answered = message || this.lastTurnMessage();
    if (message) {
      if (stopped) { message.tools = this.takeTools(); }
      this.state.messages.push(message);
    }
    if (answered && answered.runMs === undefined) { answered.runMs = runMs; }
    if (answered && this.timings) { answered.timings = this.timings; }
    this.timings = null;
    if (stopped) {
      this.state.messages.push({ role: 'notice', text: 'Stopped.', timestamp: new Date() });
    }
    if (this.toolsNote) {
      this.state.messages.push({ role: 'notice', admin: true, text: this.toolsNote, timestamp: new Date() });
      this.toolsNote = null;
    }
    this.streamingText = '';
    this.liveTools = [];
    this.isLoading = false;
    this.abort = null;
    this.stopRunTimer();
    this.scrollToBottom();
  }

  /** The AI's message this turn already added (blocks, confirmation), if any. */
  private lastTurnMessage(): AiChatMessage | null {
    const last = this.state.messages[this.state.messages.length - 1];
    return last && (last.role === 'ai' || last.role === 'confirm') && last.timestamp.getTime() >= this.runStartedAt ? last : null;
  }

  /** "Worked through 3 steps · 4.1s" above an answer. */
  runSummary(msg: AiChatMessage): string {
    const n = msg.tools?.length ?? 0;
    if (n === 0) { return msg.runMs !== undefined ? `Answered in ${formatDuration(msg.runMs)}` : 'Answered'; }
    const steps = n === 1 ? '1 step' : `${n} steps`;
    return msg.runMs !== undefined ? `Worked through ${steps} · ${formatDuration(msg.runMs)}` : `Worked through ${steps}`;
  }

  failedTools(tools: AiChatToolStep[] | undefined): boolean {
    return !!tools?.some(t => t.ok === false);
  }

  /** "0.4s", "12s", "1m 5s". */
  duration(ms: number | undefined): string {
    return ms === undefined || ms === null ? '' : formatDuration(ms);
  }

  /** Seconds the turn under way has run. */
  get elapsed(): string {
    return this.runStartedAt ? `${Math.max(0, Math.floor((this.now - this.runStartedAt) / 1000))}s` : '';
  }

  /** How far the turn under way has got: before any tool or text, it is reading the request. */
  get understood(): boolean {
    return this.liveTools.length > 0 || !!this.streamingText;
  }

  /** It is writing once text streams in, or every tool it started has finished. */
  get writing(): boolean {
    return !!this.streamingText || (this.liveTools.length > 0 && this.liveTools.every(t => t.ok !== null));
  }

  private startRunTimer(): void {
    this.stopRunTimer();
    this.runStartedAt = Date.now();
    this.now = this.runStartedAt;
    this.ngZone.runOutsideAngular(() => {
      this.runTimer = setInterval(() => this.ngZone.run(() => { this.now = Date.now(); }), 500);
    });
  }

  private stopRunTimer(): void {
    if (this.runTimer) {
      clearInterval(this.runTimer);
      this.runTimer = null;
    }
  }

  getRecordFields(fields: Record<string, string>): { key: string; value: string }[] {
    return Object.entries(fields).map(([key, value]) => ({ key, value }));
  }

  isLink(value: string): boolean {
    return value?.startsWith('http://') || value?.startsWith('https://');
  }

  recordHeaders(records: RecordItem[]): string[] {
    const keys: string[] = [];
    const seen = new Set<string>();
    for (const r of records) {
      for (const k of Object.keys(r.fields || {})) {
        if (!seen.has(k)) { seen.add(k); keys.push(k); }
      }
    }
    return keys.slice(0, 5);
  }

  recordPrimaryValue(record: RecordItem, key: string): string {
    return record.fields?.[key] ?? '';
  }

  /** Field rendering helpers for the multi-record row layout.
   *  We pick a primary value (first non-empty field), a status-like field
   *  (key matches /status/i) to render as a pill, and treat the rest as
   *  small metadata items below. */
  private fieldEntries(record: RecordItem): { key: string; value: string }[] {
    return Object.entries(record.fields || {})
      .map(([key, value]) => ({ key, value: value ?? '' }))
      .filter(e => e.value !== '');
  }

  private isStatusKey(key: string): boolean {
    return /status/i.test(key);
  }

  recordRowPrimary(record: RecordItem): { key: string; value: string } | null {
    const entries = this.fieldEntries(record).filter(e => !this.isStatusKey(e.key));
    // Prefer an amount/total/price-like field so a record that puts amount
    // later (or first puts vendor name) still surfaces the money value.
    const amountish = entries.find(e => /amount|total|price/i.test(e.key));
    return amountish ?? entries[0] ?? null;
  }

  recordRowTitle(record: RecordItem): string {
    if (record.title) return record.title;
    const entries = this.fieldEntries(record).filter(e => !this.isStatusKey(e.key));
    return entries[1]?.value ?? '';
  }

  recordRowMeta(record: RecordItem): { key: string; value: string }[] {
    const entries = this.fieldEntries(record).filter(e => !this.isStatusKey(e.key));
    // Skip the two fields already used as primary / title; cap at 3 to keep
    // the row compact in the side panel.
    const skipCount = record.title ? 1 : 2;
    return entries.slice(skipCount, skipCount + 3);
  }

  recordRowStatus(record: RecordItem): string {
    const status = this.fieldEntries(record).find(e => this.isStatusKey(e.key));
    return status?.value ?? '';
  }

  /** Vendor / supplier name for a single record. Prefers an explicit
   *  vendor-like field, then strips the trailing " — $X.XX" suffix from
   *  the title (the AI often composes title as "Vendor — Amount"). */
  recordVendor(record: RecordItem): string {
    const vendorEntry = this.fieldEntries(record)
      .find(e => /^(vendor|supplier|biller|contact|name)$/i.test(e.key));
    if (vendorEntry?.value) return vendorEntry.value;
    const raw = record.title || '';
    const stripped = raw.replace(/\s+[—–\-]\s+[^\s—–-]*\d[\d,.]*\s*$/, '').trim();
    return stripped || raw;
  }

  /** Amount/total for a single record. Prefers AMOUNT/TOTAL fields, falls
   *  back to extracting "$X.XX" from the title. */
  recordTotal(record: RecordItem): string {
    const amountEntry = this.fieldEntries(record)
      .find(e => /amount|total|price/i.test(e.key));
    if (amountEntry?.value) return amountEntry.value;
    const raw = record.title || '';
    const m = raw.match(/[—–\-]\s+([^\s—–-]*\d[\d,.]*)\s*$/);
    return m ? m[1] : '';
  }

  /** Meta fields for the row's second line — date, description/category,
   *  confidence. Drops vendor (shown on top row), amount (also on top),
   *  status, and any field whose value duplicates the vendor we already
   *  rendered. */
  recordRowMetaFiltered(record: RecordItem): { key: string; value: string }[] {
    const vendor = this.recordVendor(record).toLowerCase();
    return this.fieldEntries(record)
      .filter(e => !this.isStatusKey(e.key))
      .filter(e => !/amount|total|price/i.test(e.key))
      .filter(e => !/^(vendor|supplier|biller|contact|name)$/i.test(e.key))
      .filter(e => e.value.toLowerCase() !== vendor)
      .slice(0, 3);
  }

  /** Group consecutive records that share an invoice (same url, or matching
   *  invoiceId field). A group with >1 records renders as one combined card
   *  showing all its lines; groups of 1 render as the regular row. */
  private groupCache = new WeakMap<RecordItem[], RecordGroup[]>();
  groupRecordsByInvoice(records: RecordItem[]): RecordGroup[] {
    const cached = this.groupCache.get(records);
    if (cached) return cached;

    const groups: RecordGroup[] = [];
    const indexByKey = new Map<string, number>();
    records.forEach((r, idx) => {
      const key = this.invoiceKey(r) || `__solo_${idx}`;
      const existing = indexByKey.get(key);
      if (existing != null) {
        groups[existing].records.push(r);
      } else {
        indexByKey.set(key, groups.length);
        groups.push({ key, records: [r] });
      }
    });
    this.groupCache.set(records, groups);
    return groups;
  }

  private invoiceKey(r: RecordItem): string {
    if (r.id) return `id:${r.id}`;
    if (r.url) return `url:${r.url}`;
    const f = r.fields || {};
    const invoiceId = f['invoiceId'] || f['InvoiceId'] || f['invoice_id'] || f['InvoiceID'];
    return invoiceId ? `inv:${invoiceId}` : '';
  }

  /** Title shared by a grouped invoice — strips the trailing " — $X" suffix
   *  that often appears on per-line record titles. */
  groupTitle(records: RecordItem[]): string {
    const raw = records[0]?.title || this.recordRowTitle(records[0]) || '';
    return raw.replace(/\s+[—–\-]\s+[^\s—–-]*\d[\d,.]*\s*$/, '').trim() || raw;
  }

  /** Sum of the primary (amount-like) field across a grouped invoice's lines. */
  groupTotal(records: RecordItem[]): string {
    let total = 0;
    let prefix = '';
    let parsed = 0;
    for (const r of records) {
      const primary = this.recordRowPrimary(r);
      if (!primary) continue;
      const m = primary.value.match(/^\s*([^\d\-.,]*)\s*(-?[\d,]+\.?\d*)/);
      if (!m) continue;
      if (!prefix) prefix = m[1].trim();
      const n = parseFloat(m[2].replace(/,/g, ''));
      if (!isNaN(n)) { total += n; parsed++; }
    }
    if (!parsed) return '';
    const formatted = total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return prefix ? `${prefix}${formatted}` : formatted;
  }

  /** Shared date across a grouped invoice's lines (first DATE-keyed field). */
  groupDate(records: RecordItem[]): string {
    for (const r of records) {
      const d = this.fieldEntries(r).find(e => /date/i.test(e.key));
      if (d?.value) return d.value;
    }
    return '';
  }

  /** Per-line description shown inside a grouped card — excludes status,
   *  date and amount-like fields so we don't repeat the header. Prefers
   *  description/category-like keys so we don't accidentally surface the
   *  vendor name as a "description". */
  groupLineDescription(record: RecordItem): string {
    const entries = this.fieldEntries(record)
      .filter(e => !this.isStatusKey(e.key))
      .filter(e => !/date|amount|total|price|confidence|vendor|supplier|biller|name/i.test(e.key));
    const preferred = entries.find(e => /description|category|memo|note|narration|detail/i.test(e.key));
    return preferred?.value || entries[0]?.value || '';
  }

  /** Returns line items for a grouped card with redundant text already
   *  trimmed: amounts/descriptions that duplicate the group title (or
   *  duplicate each other) are blanked. If no line has anything unique to
   *  show, returns an empty array so the lines section can be hidden. */
  groupLines(records: RecordItem[]): { amount: string; description: string }[] {
    const title = this.groupTitle(records);
    const raw = records.map(r => ({
      amount: this.recordRowPrimary(r)?.value || '',
      description: this.groupLineDescription(r) || '',
    }));
    const cleaned = raw.map(l => ({
      amount: l.amount && l.amount !== title ? l.amount : '',
      description:
        l.description && l.description !== title && l.description !== l.amount
          ? l.description
          : '',
    }));
    const meaningful = cleaned.some(l => l.amount || l.description);
    return meaningful ? cleaned : [];
  }

  recordHref(record: RecordItem): string | null {
    // Invoice-like records route through the inbox edit page using the
    // record's id (which the AI returns as the invoice id). Fall back to
    // the API's own url for non-invoice entity types if it's present.
    if (record.id) return `/inbox/editInvoice/${record.id}`;
    const url = record.url;
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return url.startsWith('/') ? url : `/${url}`;
  }

  openRecord(record: RecordItem, event?: MouseEvent): void {
    const href = this.recordHref(record);
    if (!href) return;
    if (event) { event.preventDefault(); event.stopPropagation(); }
    window.open(href, '_blank', 'noopener');
  }

  onEnter(event: KeyboardEvent): void {
    if (!event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  /** ↑ recalls the previous sent message (shell-style). Only hijacks the key
   *  when the caret is at the very start, so multi-line editing still works. */
  recallPrevious(event: KeyboardEvent): void {
    const el = event.target as HTMLTextAreaElement;
    if (el.selectionStart !== 0 || el.selectionEnd !== 0) return;
    if (!this.state.inputHistory.length) return;
    event.preventDefault();
    if (this.historyIndex === -1) {
      this.draftBeforeHistory = this.inputText;
      this.historyIndex = this.state.inputHistory.length - 1;
    } else if (this.historyIndex > 0) {
      this.historyIndex--;
    }
    this.inputText = this.state.inputHistory[this.historyIndex];
    this.afterRecall(el);
  }

  /** ↓ moves forward through history, returning to the live draft at the end.
   *  Only active while navigating and when the caret is at the end. */
  recallNext(event: KeyboardEvent): void {
    if (this.historyIndex === -1) return;
    const el = event.target as HTMLTextAreaElement;
    if (el.selectionStart !== el.value.length || el.selectionEnd !== el.value.length) return;
    event.preventDefault();
    if (this.historyIndex < this.state.inputHistory.length - 1) {
      this.historyIndex++;
      this.inputText = this.state.inputHistory[this.historyIndex];
    } else {
      this.historyIndex = -1;
      this.inputText = this.draftBeforeHistory;
    }
    this.afterRecall(el);
  }

  // Puts the caret at the end of the recalled text and re-fits the textarea
  // height (programmatic value changes don't fire the input/resize handler).
  private afterRecall(el: HTMLTextAreaElement): void {
    setTimeout(() => {
      const len = el.value.length;
      el.setSelectionRange(len, len);
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    });
  }

  autoResize(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    // Typing commits to a new draft — exit history navigation.
    this.historyIndex = -1;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) this.close();
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 50);
  }

  private resetInputHeight(): void {
    if (this.inputRef?.nativeElement) {
      this.inputRef.nativeElement.style.height = 'auto';
    }
  }
}
